import { useState, useEffect } from "react";
import { Filter, Check } from "lucide-react";
import { Button } from "@component/button";
import { Label } from "@component/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@component/popover";
import { AssessmentFilterState } from "@type/job";


interface AssessmentFilterPanelProps {
    filters: AssessmentFilterState;
    onFiltersChange: (filters: AssessmentFilterState) => void;
    className?: string;
}

export default function AssessmentFilterPanel({
    filters,
    onFiltersChange,
    className = "",
}: AssessmentFilterPanelProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({
        status: filters.status || [],
    });
    const [isSaved, setIsSaved] = useState(false);

    // Load saved filters from localStorage on mount
    useEffect(() => {
        const savedFilters = localStorage.getItem("assessmentFilters");
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                const normalizedFilters = {
                    assessmentTypes: parsed.assessmentTypes || [],
                    status: parsed.status || [],
                };
                setTempFilters(normalizedFilters);
            } catch (error) {
                console.error("Error loading saved filters:", error);
            }
        }
    }, []);

    const statusOptions = [
        { value: "Active", label: "Active" },
        { value: "Draft", label: "Draft" },
    ];

    const handleStatusChange = (value: string, checked: boolean) => {
        const newFilters = {
            status: tempFilters.status || [],
        };
        if (checked) {
            if (!newFilters.status.includes(value)) {
                newFilters.status = [...newFilters.status, value];
            }
        } else {
            newFilters.status = newFilters.status.filter((item) => item !== value);
        }
        setTempFilters(newFilters);
    };

    const saveFilters = () => {
        localStorage.setItem("assessmentFilters", JSON.stringify(tempFilters));
        onFiltersChange(tempFilters);

        // Show "Saved!" state for 2 seconds
        setIsSaved(true);
        setTimeout(() => {
            setIsSaved(false);
        }, 2000);
    };

    const clearAllFilters = () => {
        const clearedFilters = { assessmentTypes: [], status: [] };
        localStorage.removeItem("assessmentFilters");
        setTempFilters(clearedFilters);
        onFiltersChange(clearedFilters);
    };

    const getTotalActiveFilters = () => {
        return (
            (filters.status || []).length
        );
    };

    const activeFiltersCount = getTotalActiveFilters();

    return (
        <div className={className}>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        className="bg-white border-gray-200 relative"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filter
                        {activeFiltersCount > 0 && (
                            <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                                {activeFiltersCount}
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                    <div className="p-3">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-sm">Filter by</h3>
                            {activeFiltersCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearAllFilters}
                                    className="text-blue-600 hover:text-blue-700 text-xs h-auto p-0"
                                >
                                    Clear all ({activeFiltersCount})
                                </Button>
                            )}
                        </div>

                        {/* Screening Status */}
                        <div>
                            <h4 className="font-semibold text-sm mb-2 text-gray-900">
                                Screening Status
                            </h4>
                            <div className="space-y-1 mb-4">
                                {statusOptions.map((option) => (
                                    <div
                                        key={option.value}
                                        className="flex items-center space-x-2 py-1 cursor-pointer"
                                        onClick={() => {
                                            const isCurrentlyChecked = (
                                                tempFilters.status || []
                                            ).includes(option.value);
                                            handleStatusChange(option.value, !isCurrentlyChecked);
                                        }}
                                    >
                                        <div className="relative flex items-center">
                                            <div
                                                className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors ${(tempFilters.status || []).includes(option.value)
                                                    ? "bg-blue-600 border-blue-600"
                                                    : "border-gray-300 hover:border-gray-400"
                                                    }`}
                                            >
                                                {tempFilters.status.includes(option.value) && (
                                                    <Check className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                        </div>

                                        <Label
                                            className={`text-sm cursor-pointer transition-colors ${(tempFilters.status || []).includes(option.value)
                                                ? "font-medium text-gray-900"
                                                : "font-normal text-gray-700"
                                                }`}
                                        >
                                            {option.label}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="pt-2 border-t">
                            <Button
                                onClick={saveFilters}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-8 text-sm"
                                size="sm"
                                disabled={isSaved}
                            >
                                {isSaved ? "Saved!" : "Save Changes"}
                            </Button>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
